/**
 * Proactively notice when a newer frontend build has been deployed while this
 * tab kept running the old one. Reports the deployed entry hash; it never
 * reloads and shows nothing (Q-506 card 1573). Acting on it is
 * `useDeployedBuildReload`, on the next navigation only.
 *
 * Checks at three moments, all cheap: once on mount, whenever the tab regains
 * focus (`visibilitychange`), and on a slow interval as a backstop.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { currentEntryHash, fetchDeployedEntryHash, isNewerDeploy } from '@/api/deployedVersion';

/** Backstop poll interval. Focus/visibility is the primary trigger. */
const POLL_MS = 5 * 60 * 1000;

/** The deployed entry hash this tab is behind, or `null` while it is current or unknown. */
export function useDeployedVersionCheck(): string | null {
  const [staleHash, setStaleHash] = useState<string | null>(null);
  const runningRef = useRef<string | null>(currentEntryHash());

  const check = useCallback(async () => {
    // No hashed entry (dev server / test) ⇒ nothing to compare, stay silent.
    if (!runningRef.current) return;
    const deployed = await fetchDeployedEntryHash();
    if (!isNewerDeploy(runningRef.current, deployed)) return;
    setStaleHash(deployed);
  }, []);

  useEffect(() => {
    void check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    const id = window.setInterval(() => void check(), POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(id);
    };
  }, [check]);

  return staleHash;
}
